<template>
  <section class="take-next-step pb-space-lg pb-lg-space-xxl">
    <div class="container px-space-xs take-next-step__container">
      <div
        class="take-next-step__panel d-flex align-items-center justify-content-center text-center"
      >
        <img
          :src="image"
          :alt="imageAlt"
          class="take-next-step__image d-none d-lg-block w-100 h-100"
          loading="lazy"
        />
        <div class="take-next-step__content py-space-md">
          <h2 class="take-next-step__title h3-xl mb-space-xs">
            Take the
            <span class="take-next-step__highlight bg-secondary p-space-xxxs">next step</span>
          </h2>
          <div class="take-next-step__description fs-medium mb-space-sm" v-html="description"></div>
          <div
            class="take-next-step__actions d-flex flex-column flex-md-row align-items-center justify-content-center px-space-xs"
          >
            <a
              :href="coachCtaHref"
              :target="coachCta.target"
              rel="noopener noreferrer"
              class="btn btn-primary fw-bold fs-medium py-space-xs px-space-md"
              tabindex="0"
              @click="handleCtaClick"
            >
              <span class="d-inline">{{ coachCta.text }}</span>
            </a>
            <ButtonApplyNow
              class="take-next-step__apply-button"
              variant="white"
              :text="applyCta.text"
              event-region="main content"
              event-section="take the next step"
              event-component="take the next step"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { computed } from "vue";

import { useRoute } from "#imports";
import ButtonApplyNow from "~/components/nav/ButtonApplyNow.vue";
import { buildTrackedCalendlyUrl } from "~/utils/calendlyTracking";

interface Props {
  description: string;
  image: string;
  imageAlt: string;
}

defineProps<Props>();

const emit = defineEmits<{
  (e: "ctaClick", text: string): void;
}>();

const coachCta = {
  text: "Connect with a coach",
  href: "https://calendly.com/asu-online-cost-appointments",
  target: "_blank"
};

const route = useRoute();
const coachCtaHref = computed(() => getTrackedCoachCtaHref());

const getTrackedCoachCtaHref = () =>
  buildTrackedCalendlyUrl(
    coachCta.href,
    route.path,
    typeof document !== "undefined" ? document.cookie : ""
  );

const applyCta = {
  text: "Start your application"
};

const handleCtaClick = (event: MouseEvent) => {
  const target = event.currentTarget as HTMLAnchorElement | null;

  if (target) {
    target.href = getTrackedCoachCtaHref();
  }

  emit("ctaClick", coachCta.text);
};
</script>

<style lang="scss" scoped>
.take-next-step {
  &__panel {
    position: relative;
    min-height: 250px;
    overflow: hidden;
  }

  &__image {
    position: absolute;
    inset: 0;
    z-index: 0;
    object-fit: contain;
    object-position: center;
    pointer-events: none;
  }

  &__content {
    position: relative;
    z-index: 1;
    width: min(100%, 620px);
  }

  &__highlight {
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
  }

  &__actions {
    gap: var(--rds-space-xs, 16px);
  }

  &__description :deep(p:last-child) {
    margin-bottom: 0;
  }

  &__apply-button :deep(.btn) {
    border-color: var(--rds-dark-3, #191919);
    background-color: transparent;
    color: var(--rds-dark-3, #191919);
  }

  &__apply-button :deep(.btn:hover),
  &__apply-button :deep(.btn:focus) {
    background-color: var(--rds-dark-3, #191919);
    color: var(--rds-white, #ffffff);
  }

  :deep(.btn) {
    min-width: 230px;
    border-radius: 999px;
    padding: var(--rds-space-xs, 16px) var(--rds-space-md, 48px);
  }
}

@media (min-width: 992px) {
  .take-next-step__container {
    max-width: 960px;
  }
}

@media (max-width: 991px) {
  .take-next-step {
    padding-top: 0;
    padding-bottom: var(--rds-space-md, 32px);

    &__panel {
      align-items: flex-start !important;
      background-color: var(--rds-light-1, #e8e8e8);
      border: solid 1px var(--rds-light-4, #d0d0d0);
    }

    &__image {
      display: none;
    }

    &__content {
      width: 100%;
    }

    &__title {
      line-height: 1.15;
      white-space: nowrap;
    }

    &__description :deep(p) {
      line-height: 1.35;
    }

    &__actions {
      flex-direction: column !important;
      gap: clamp(22px, 4.5vw, 34px);
      padding: 16px;
    }

    &__apply-button {
      width: 100%;
      max-width: 690px;
    }

    :deep(.btn) {
      width: 100%;
      max-width: 690px;
      min-width: 0;
    }
  }
}
</style>
